import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {IoCloseCircleOutline} from 'react-icons/io5';
import {useNavigate, useSearchParams} from 'react-router-dom';
import addeparManager from '../../api/addeparManager';
import {useAddepar} from '../../hooks/use-adepar';
import {useAuth} from '../../hooks/use-auth';
import './AddeparConnect.css';

const AddeparConnect = () => {
  const {
    state: {profile},
  } = useAuth();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {createConnection} = useAddepar();

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [oauthData, setOauthData] = useState(null);
  const [authCode, setAuthCode] = useState(null);
  const [entityMapping, setEntityMapping] = useState([]);
  const [currentEntityMapping, setCurrentEntityMapping] = useState({
    entity_name: '',
    entity_id: '',
  });
  const [accountMappings, setAccountMappings] = useState([]);
  const [currentAccountMapping, setCurrentAccountMapping] = useState({
    account_name: '',
    owner_id: '',
    entity_id: '',
    entity_name: '',
  });
  const [mappingError, setMappingError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [currentStep, setCurrentStep] = useState('entity'); // 'entity' or 'account'

  const hasExchangedRef = useRef(false);

  const handleOAuthConnection = useCallback(async code => {
    try {
      if (!code) {
        setError('No authorization code received from Addepar');
        setStatus('error');
        return;
      }

      if (hasExchangedRef.current) {
        return;
      }

      setProgress(20);
      setStatus('exchanging_code');

      const redirectUri =
        process.env.REACT_APP_ADDEPAR_REDIRECT_URI ||
        'https://forest-systems-plaid-dev.web.app/addeparConnect';
      // Exchange the authorization code for access token
      const tokenResponse = await addeparManager.exchangeCodeForToken(
        code,
        redirectUri,
      );

      setProgress(50);

      if (!tokenResponse?.success) {
        throw new Error('Failed to exchange code for token');
      }

      // Store OAuth data for later use
      const oauthConnectionData = {
        ...(tokenResponse?.data ?? {}),
        connection_date: new Date().toISOString(),
        status: 'active',
      };

      setOauthData(oauthConnectionData);
      hasExchangedRef.current = true;
      setStatus('mapping');
      setProgress(100);
    } catch (err) {
      console.error('Addepar OAuth connection error:', err);
      setError(err.message || 'Failed to complete Addepar connection');
      setStatus('error');
    }
  }, []);

  // Validate the current entity mapping
  const validateEntityMapping = mapping => {
    return mapping.entity_name && mapping.entity_id;
  };

  // Validate the current account mapping
  const validateAccountMapping = mapping => {
    return (
      mapping.account_name &&
      mapping.owner_id &&
      mapping.entity_id &&
      mapping.entity_name
    );
  };

  // Update current entity mapping field
  const updateCurrentEntityMapping = (field, value) => {
    setCurrentEntityMapping(prev => ({
      ...prev,
      [field]: value,
    }));
    setMappingError(''); // Clear any previous errors when user makes changes
  };

  // Update current account mapping field
  const updateCurrentAccountMapping = (field, value) => {
    setCurrentAccountMapping(prev => ({
      ...prev,
      [field]: value,
    }));
    setMappingError(''); // Clear any previous errors when user makes changes
  };

  // Add entity mapping to list
  const addEntityMapping = async () => {
    if (!validateEntityMapping(currentEntityMapping)) {
      setMappingError(
        'Please fill out all fields before adding an entity mapping',
      );
      return;
    }

    setIsValidating(true);
    setMappingError('');

    try {
      // If validation passes, add the entity mapping to the list
      setEntityMapping(prev => [...prev, currentEntityMapping]);
      setCurrentEntityMapping({
        entity_name: '',
        entity_id: '',
      });
      setMappingError('');
      // Move to account mapping step
      setCurrentStep('account');
    } catch (err) {
      setMappingError('Failed to add entity mapping');
      console.error('Entity mapping error:', err);
    } finally {
      setIsValidating(false);
    }
  };

  // Add account mapping to list
  const addAccountMapping = async () => {
    if (!validateAccountMapping(currentAccountMapping)) {
      setMappingError(
        'Please fill out all fields before adding an account mapping',
      );
      return;
    }

    setIsValidating(true);
    setMappingError('');

    try {
      // If validation passes, add the account mapping to the list
      setAccountMappings(prev => [...prev, currentAccountMapping]);
      setCurrentAccountMapping({
        account_name: '',
        owner_id: '',
        entity_id: '',
        entity_name: '',
      });
      setMappingError('');
    } catch (err) {
      setMappingError('Failed to add account mapping');
      console.error('Account mapping error:', err);
    } finally {
      setIsValidating(false);
    }
  };

  // Remove account mapping from list
  const removeAccountMapping = index => {
    setAccountMappings(prev => prev.filter((_, i) => i !== index));
  };

  // Finalize all mappings and save connection
  const finalizeConnection = async () => {
    if (entityMapping.length === 0) {
      setMappingError(
        'Please add at least one entity mapping before finalizing',
      );
      return;
    }

    if (accountMappings.length === 0) {
      setMappingError(
        'Please add at least one account mapping before finalizing',
      );
      return;
    }

    setStatus('saving_connection');
    setProgress(0);

    try {
      // Create connection object with OAuth data and mappings
      const connectionData = {
        ...oauthData,
        uid: profile.uid,
        customerID: profile.customerID,
        entity_mappings: entityMapping,
        account_mappings: accountMappings,
        connection_type: 'oauth', // Distinguish from API key connections
      };

      // Save to database
      const saveResult = await createConnection(connectionData);
      setProgress(100);

      if (saveResult.success) {
        setStatus('success');
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      } else {
        throw new Error('Failed to save connection to database');
      }
    } catch (err) {
      console.error('Failed to save connection:', err);
      setError(err.message || 'Failed to save connection');
      setStatus('error');
    }
  };

  // Effect 1: read code from URL and store in state exactly once per URL change
  useEffect(() => {
    const code = searchParams.get('code');
    setAuthCode(code);
  }, [searchParams]);

  // Effect 2: when we have a code, perform the exchange once
  useEffect(() => {
    if (!authCode) return;
    if (hasExchangedRef.current) return;
    handleOAuthConnection(authCode);
  }, [authCode]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <h2>Connecting to Addepar...</h2>
            <div className="loading-spinner"></div>
            <p>Please wait while we establish your connection.</p>
          </div>
        );

      case 'waiting_for_auth':
        return (
          <div className="text-center">
            <h2>Authenticating...</h2>
            <div className="loading-spinner"></div>
            <p>Please wait while we verify your authentication.</p>
          </div>
        );

      case 'exchanging_code':
        return (
          <div className="text-center">
            <h2>Exchanging Authorization Code</h2>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{width: `${progress}%`}}></div>
            </div>
            <p>Securing your connection with Addepar...</p>
          </div>
        );

      case 'mapping':
        return (
          <div>
            <Typography sx={{textAlign: 'center', fontSize: '22px', mb: 2}}>
              Configure Account Mappings
            </Typography>
            <Typography sx={{textAlign: 'center', mb: 3}}>
              OAuth connection successful! Now configure your account mappings.
            </Typography>

            {mappingError && (
              <Typography sx={{textAlign: 'center', color: 'red', mb: 2}}>
                {mappingError}
              </Typography>
            )}

            {/* Entity Mapping Section - Only show on entity step */}
            {currentStep === 'entity' && (
              <Box
                sx={{mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 1}}>
                <Typography variant="h6" sx={{mb: 2}}>
                  Entity Mapping
                </Typography>
                <Typography
                  variant="body2"
                  sx={{mb: 2, color: 'text.secondary'}}>
                  Add the primary entity for this connection
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        label="Entity Name"
                        placeholder="Entity Name"
                        value={currentEntityMapping.entity_name}
                        onChange={evt =>
                          updateCurrentEntityMapping(
                            'entity_name',
                            evt.target.value,
                          )
                        }
                        variant="filled"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        label="Entity ID"
                        placeholder="Entity ID"
                        value={currentEntityMapping.entity_id}
                        onChange={evt =>
                          updateCurrentEntityMapping(
                            'entity_id',
                            evt.target.value,
                          )
                        }
                        variant="filled"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Box sx={{mt: 2, display: 'flex', justifyContent: 'center'}}>
                  <Button
                    variant="contained"
                    onClick={addEntityMapping}
                    disabled={isValidating}
                    sx={{
                      backgroundColor: '#c5c7c6',
                      color: 'black',
                      '&:hover': {
                        backgroundColor: '#b0b2b1',
                      },
                    }}>
                    {isValidating ? 'Validating...' : 'Add Entity Mapping'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Account Mapping Section - Only show on account step */}
            {currentStep === 'account' && (
              <Box
                sx={{mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 1}}>
                <Typography variant="h6" sx={{mb: 2}}>
                  Account Mapping
                </Typography>
                <Typography
                  variant="body2"
                  sx={{mb: 2, color: 'text.secondary'}}>
                  Add account mappings for this connection
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        label="Entity Name"
                        placeholder="Entity Name"
                        value={currentAccountMapping.entity_name}
                        onChange={evt =>
                          updateCurrentAccountMapping(
                            'entity_name',
                            evt.target.value,
                          )
                        }
                        variant="filled"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        label="Entity ID"
                        placeholder="Entity ID"
                        value={currentAccountMapping.entity_id}
                        onChange={evt =>
                          updateCurrentAccountMapping(
                            'entity_id',
                            evt.target.value,
                          )
                        }
                        variant="filled"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        label="Account Name"
                        placeholder="Account Name"
                        value={currentAccountMapping.account_name}
                        onChange={evt =>
                          updateCurrentAccountMapping(
                            'account_name',
                            evt.target.value,
                          )
                        }
                        variant="filled"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        label="Owner ID"
                        placeholder="Owner ID"
                        value={currentAccountMapping.owner_id}
                        onChange={evt =>
                          updateCurrentAccountMapping(
                            'owner_id',
                            evt.target.value,
                          )
                        }
                        variant="filled"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Box sx={{mt: 2, display: 'flex', justifyContent: 'center'}}>
                  <Button
                    variant="contained"
                    onClick={addAccountMapping}
                    disabled={isValidating}
                    sx={{
                      backgroundColor: '#c5c7c6',
                      color: 'black',
                      '&:hover': {
                        backgroundColor: '#b0b2b1',
                      },
                    }}>
                    {isValidating ? 'Validating...' : 'Add Account Mapping'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Account Mappings List */}
            {accountMappings.length > 0 && (
              <Box sx={{mb: 3}}>
                <Typography variant="h6" sx={{mb: 2}}>
                  Account Mappings ({accountMappings.length})
                </Typography>
                <List sx={{border: '1px solid #ccc', borderRadius: 1}}>
                  {accountMappings.map((mapping, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        borderBottom:
                          index < accountMappings.length - 1
                            ? '1px solid #eee'
                            : 'none',
                      }}>
                      <ListItemText
                        primary={`${mapping.account_name}`}
                        secondary={`Owner ID: ${mapping.owner_id}, Entity ID: ${mapping.entity_id}, Entity Name: ${mapping.entity_name}`}
                      />
                      <IconButton
                        onClick={() => removeAccountMapping(index)}
                        color="error"
                        size="small">
                        <IoCloseCircleOutline />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Finalize Button - Only show on account step */}
            {currentStep === 'account' && (
              <Box sx={{display: 'flex', justifyContent: 'center', gap: 2}}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    borderColor: '#c5c7c6',
                    color: 'black',
                    '&:hover': {
                      borderColor: '#b0b2b1',
                      backgroundColor: '#f5f5f5',
                    },
                  }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={finalizeConnection}
                  disabled={accountMappings.length === 0}
                  sx={{
                    backgroundColor: '#c5c7c6',
                    color: 'black',
                    '&:hover': {
                      backgroundColor: '#b0b2b1',
                    },
                  }}>
                  Finalize Connection
                </Button>
              </Box>
            )}
          </div>
        );

      case 'saving_connection':
        return (
          <div className="text-center">
            <h2>Saving Connection</h2>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{width: `${progress}%`}}></div>
            </div>
            <p>Storing your connection details...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center success-message">
            <h2>✅ Connection Successful!</h2>
            <p>Your Addepar account has been successfully connected.</p>
            <p>Redirecting to dashboard...</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center error-message">
            <h2>❌ Connection Failed</h2>
            <p>{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="retry-button">
              Return to Dashboard
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="addepar-connect-container">
      <div className="addepar-connect-card">
        <div className="addepar-logo">
          <h1>Addepar Connection</h1>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default AddeparConnect;
